import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const DataIntegrationPanel = ({ vessel }) => {
  const [connectionStatus, setConnectionStatus] = useState({
    ais: { status: 'connected', lastSync: '2 min ago', quality: 98 },
    vms: { status: 'connected', lastSync: '1 min ago', quality: 95 },
    iot: { status: 'warning', lastSync: '5 min ago', quality: 87 },
    noon: { status: 'connected', lastSync: '30 min ago', quality: 92 },
    weather: { status: 'connected', lastSync: '15 min ago', quality: 99 }
  });

  const [isRefreshing, setIsRefreshing] = useState(false);

  const dataSources = [
    {
      id: 'ais',
      name: 'AIS Tracking',
      description: 'Real-time position and navigation data',
      icon: 'Radar',
      provider: 'MarineTraffic API'
    },
    {
      id: 'vms',
      name: 'VMS Integration',
      description: 'Voyage Management System data sync',
      icon: 'Database',
      provider: 'Veson IMOS'
    },
    {
      id: 'iot',
      name: 'IoT Fuel Sensors',
      description: 'Real-time bunker consumption monitoring',
      icon: 'Cpu',
      provider: 'Bunker Sensors Network'
    },
    {
      id: 'noon',
      name: 'Noon Reports',
      description: 'Daily operational reports and logs',
      icon: 'FileText',
      provider: 'Ship Management System'
    },
    {
      id: 'weather',
      name: 'Weather Routing',
      description: 'Meteorological and oceanographic data',
      icon: 'Cloud',
      provider: 'Weather API'
    }
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case 'connected':
        return 'text-success bg-success/10 border-success/20';
      case 'warning':
        return 'text-warning bg-warning/10 border-warning/20';
      case 'error':
        return 'text-error bg-error/10 border-error/20';
      case 'syncing':
        return 'text-accent bg-accent/10 border-accent/20';
      default:
        return 'text-muted-foreground bg-muted border-border';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'connected':
        return 'CheckCircle';
      case 'warning':
        return 'AlertTriangle';
      case 'error':
        return 'XCircle';
      case 'syncing':
        return 'RefreshCw';
      default:
        return 'Circle';
    }
  };

  const getQualityColor = (quality) => {
    if (quality >= 95) return 'text-success';
    if (quality >= 85) return 'text-warning';
    return 'text-error';
  };

  const handleRefreshAll = async () => {
    setIsRefreshing(true);
    
    // Simulate refresh process
    setTimeout(() => {
      setConnectionStatus(prev => ({
        ...prev,
        ais: { ...prev?.ais, lastSync: 'Just now' },
        vms: { ...prev?.vms, lastSync: 'Just now' },
        iot: { ...prev?.iot, status: 'connected', lastSync: 'Just now', quality: 96 },
        noon: { ...prev?.noon, lastSync: 'Just now' },
        weather: { ...prev?.weather, lastSync: 'Just now' }
      }));
      setIsRefreshing(false);
    }, 2000);
  };

  const handleTestConnection = (sourceId) => {
    setConnectionStatus(prev => ({
      ...prev,
      [sourceId]: { ...prev?.[sourceId], status: 'syncing' }
    }));

    setTimeout(() => {
      setConnectionStatus(prev => ({
        ...prev,
        [sourceId]: { ...prev?.[sourceId], status: 'connected', lastSync: 'Just now' }
      }));
    }, 1500);
  };

  useEffect(() => {
    // Simulate real-time updates
    const interval = setInterval(() => {
      setConnectionStatus(prev => {
        const updated = { ...prev };
        Object.keys(updated)?.forEach(key => {
          if (Math.random() > 0.9) { // 10% chance to update
            updated[key] = {
              ...updated?.[key],
              lastSync: Math.floor(Math.random() * 5) + 1 + ' min ago'
            };
          }
        });
        return updated;
      });
    }, 30000);

    return () => clearInterval(interval);
  }, []);

  const connectedSources = Object.values(connectionStatus)?.filter(s => s?.status === 'connected')?.length;
  const averageQuality = Math.round(
    Object.values(connectionStatus)?.reduce((sum, s) => sum + s?.quality, 0) / 
    Object.values(connectionStatus)?.length
  );

  return (
    <div className="bg-card rounded-lg border p-6 shadow-maritime-sm">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-foreground">Data Integration Status</h3>
          <p className="text-sm text-muted-foreground">Real-time connectivity with external systems</p>
        </div>
        
        <div className="flex items-center space-x-4">
          <div className="text-right">
            <div className="text-sm text-muted-foreground">Data Quality</div>
            <div className={`text-lg font-bold ${getQualityColor(averageQuality)}`}>
              {averageQuality}%
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={handleRefreshAll}
            loading={isRefreshing}
            iconName="RefreshCw"
            iconPosition="left"
          >
            Refresh All
          </Button>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
        {dataSources?.map((source) => {
          const status = connectionStatus?.[source?.id];
          return (
            <div 
              key={source?.id}
              className={`p-4 rounded-lg border transition-maritime ${getStatusColor(status?.status)}`}
            >
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center space-x-3">
                  <Icon name={source?.icon} size={20} strokeWidth={2} />
                  <div>
                    <h4 className="font-medium text-foreground">{source?.name}</h4>
                    <p className="text-xs text-muted-foreground">{source?.provider}</p>
                  </div>
                </div>
                <Icon 
                  name={getStatusIcon(status?.status)} 
                  size={16} 
                  strokeWidth={2}
                  className={status?.status === 'syncing' ? 'animate-spin' : ''}
                />
              </div>
              <p className="text-sm text-muted-foreground mb-3">
                {source?.description}
              </p>
              <div className="flex items-center justify-between text-xs">
                <span className="text-muted-foreground">
                  Last sync: {status?.lastSync}
                </span>
                <div className="flex items-center space-x-2">
                  <span className={`font-medium ${getQualityColor(status?.quality)}`}>
                    {status?.quality}%
                  </span>
                  <button
                    onClick={() => handleTestConnection(source?.id)}
                    className="text-accent hover:text-accent/80 transition-maritime"
                  >
                    Test
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="p-4 bg-success/5 border border-success/20 rounded-lg">
          <div className="flex items-center space-x-2 mb-2">
            <Icon name="CheckCircle" size={16} className="text-success" strokeWidth={2} />
            <span className="text-sm font-medium text-foreground">Connected Sources</span>
          </div>
          <div className="text-2xl font-bold text-success">
            {connectedSources}/{dataSources?.length}
          </div>
        </div>
        
        <div className="p-4 bg-accent/5 border border-accent/20 rounded-lg">
          <div className="flex items-center space-x-2 mb-2">
            <Icon name="Activity" size={16} className="text-accent" strokeWidth={2} />
            <span className="text-sm font-medium text-foreground">Data Throughput</span>
          </div>
          <div className="text-2xl font-bold text-accent">
            2.4 MB/min
          </div>
        </div>
        
        <div className="p-4 bg-primary/5 border border-primary/20 rounded-lg">
          <div className="flex items-center space-x-2 mb-2">
            <Icon name="Clock" size={16} className="text-primary" strokeWidth={2} />
            <span className="text-sm font-medium text-foreground">Update Frequency</span>
          </div>
          <div className="text-2xl font-bold text-primary">
            Real-time
          </div>
        </div>
      </div>
      <div className="mt-6 p-4 bg-muted/50 rounded-lg">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Icon name="Info" size={16} className="text-accent" strokeWidth={2} />
            <span className="text-sm text-foreground">
              All data sources are automatically synchronized every 15 minutes
            </span>
          </div>
          <Button variant="ghost" size="sm" iconName="Settings" iconPosition="left">
            Configure
          </Button>
        </div>
      </div>
    </div>
  );
};

export default DataIntegrationPanel;